import React, {useState, useMemo, useEffect, useRef} from "react";
import {AutoComplete} from "antd";
import {useTranslation} from "react-i18next";
import cn from "classnames";
import {getFullName} from "helpers/helper";
import {SignContractCompanyInfoTag} from "./SignContractCompanyInfoTag";

export const SignContractMemberAutocomplete = ({
  search,
  localMembers,
  value,
  onChange,
  onSelect,
  onBlur,
  disabled,
  addClass,
  target = "name",
  placeholder = "TYPE_TO_ADD_COMPANY",
  extraData = "extra",
  fetchOnFocus = false,
}) => {
  const {t} = useTranslation();
  const [data, setData] = useState([]);
  const isLocalMembersMode = localMembers !== undefined;

  const timeoutRef = useRef();
  const handleSearch = (value) => {
    if (isLocalMembersMode) {
      const query = value?.trim().toLowerCase();
      const list = (localMembers ?? []).filter(
        (m) => !!getFullName(m.User?.firstName, m.User?.lastName).trim()
      );
      if (!query) return setData(list);
      setData(
        list.filter((m) => {
          const name = getFullName(m.User?.firstName, m.User?.lastName).toLowerCase();
          return name.includes(query);
        })
      );
    } else {
      clearTimeout(timeoutRef.current);

      timeoutRef.current = setTimeout(async () => {
        const query = value?.trim() ?? "";
        setData(await search(query, extraData));
      }, 400);
    }
  };

  useEffect(() => {
    if (isLocalMembersMode) {
      const withName = (localMembers ?? []).filter(
        (m) => getFullName(m.User?.firstName, m.User?.lastName).trim().length > 0
      );
      setData(withName);
    }
  }, [isLocalMembersMode, localMembers]);

  const handleFocus = () => {
    if (fetchOnFocus && !isLocalMembersMode && (!data || data.length === 0)) {
      search("", extraData)
        .then((res) => setData(res))
        .catch(() => {});
    }
  };

  const dataSource = useMemo(
    () =>
      isLocalMembersMode
        ? data.map((m) => ({
            value: m.id,
            text: (
              <div
                className={cn(
                  "sign-contract-member-autocomplete__option-row",
                  "sign-contract-member-autocomplete__option-row--members"
                )}
              >
                <span className="row__organization">
                  {getFullName(m.User?.firstName, m.User?.lastName)}
                </span>
              </div>
            ),
          }))
        : data?.map((item) => ({
            value:
              (target === "companyNumber" ? item.Company?.companyNumber : item.name) ??
              "",
            text: (
              <div className="sign-contract-member-autocomplete__option-row">
                <span className="row__organization">{item.name}</span>
                <div className="row__statuses">
                  <SignContractCompanyInfoTag
                    number={item.Company?.companyNumber}
                    name={item.Company?.companyName}
                    bordered
                  />
                </div>
              </div>
            ),
          })),
    [data, target, isLocalMembersMode]
  );

  const handleSelect = (val) => {
    if (isLocalMembersMode) {
      const member = localMembers.find((m) => m.id === val);
      if (onSelect && member) onSelect(member);
      return;
    }
    const selectedItem = data.find((item) =>
      target === "companyNumber" ? item.Company?.companyNumber === val : item.name === val
    );
    if (onSelect && selectedItem) onSelect(selectedItem);
  };

  const stopPropagation = (e) => e.code === "Enter" && e.stopPropagation();

  return (
    <div className={cn("sign-contract-member-autocomplete", "full-width")}>
      <AutoComplete
        bordered={false}
        className={cn("input", "pl-1", addClass)}
        value={value}
        onChange={onChange}
        onSelect={handleSelect}
        onBlur={onBlur}
        onFocus={handleFocus}
        disabled={disabled}
        onSearch={handleSearch}
        filterOption={false}
        dataSource={dataSource}
        dropdownMatchSelectWidth
        onKeyDown={stopPropagation}
        placeholder={t(placeholder)}
        dropdownClassName="recipients-tab_custom-select-dropdown sign-contract-member-autocomplete__dropdown"
      />
    </div>
  );
};
